import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Edit2, Trash2, Monitor, Mail } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { FF, FS, ErrMsg } from '../components/FormField';
import { FilterBar, SearchInput, FilterSelect, PageHeader } from '../components/FilterBar';
import SearchableSelect from '../components/SearchableSelect';

const SYS_STATUSES = ['active','disabled','password_changed'];
const EMAIL_TYPES = ['corporate','personal','shared','alias'];
const EMAIL_STATUSES = ['active','disabled','password_changed','forwarded'];

export default function AccessManagement() {
  const { isIT, isAdmin } = useAuth();
  const [tab, setTab] = useState('systems');
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); const [filterStatus, setFilterStatus] = useState('');
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [employees, setEmployees] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined, status: filterStatus || undefined };
      const endpoint = tab === 'systems' ? '/access/systems' : '/access/emails';
      const r = await api.get(endpoint, { params });
      setRows(r.data.data || []); setTotal(r.data.total || 0);
    } catch {} finally { setLoading(false); }
  }, [tab, search, filterStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/employees', { params: { limit: 500 } }).then(r => setEmployees(r.data.data || [])).catch(() => {}); }, []);

  const handleSave = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const endpoint = tab === 'systems' ? '/access/systems' : '/access/emails';
      selected ? await api.put(`${endpoint}/${selected.id}`, form) : await api.post(endpoint, form);
      setModal(null); setSelected(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    const endpoint = tab === 'systems' ? `/access/systems/${id}` : `/access/emails/${id}`;
    try { await api.delete(endpoint); load(); } catch {}
  };

  const openAdd = () => {
    setSelected(null);
    setForm(tab === 'systems' ? { status: 'active' } : { status: 'active', email_type: 'corporate' });
    setError(''); setModal('form');
  };

  const sysColumns = [
    { header: 'Employee', render: r => <div><div className="font-medium text-sm" style={{color:'#e2e8f0'}}>{r.employee?.full_name || '—'}</div><div className="text-xs" style={{color:'#334155'}}>{r.employee?.company_id || ''}</div></div> },
    { header: 'System / ERP', render: r => <span className="font-medium" style={{color:'#818cf8'}}>{r.system_name}</span> },
    { header: 'Username', render: r => <span className="font-mono text-xs" style={{color:'#94a3b8'}}>{r.username || '—'}</span> },
    { header: 'Access Level', render: r => r.access_level || <span style={{color:'#334155'}}>—</span> },
    { header: 'Approved By', render: r => <span style={{color:'#60a5fa'}}>{r.approved_by || '—'}</span> },
    { header: 'Status', render: r => <Badge value={r.status}/> },
    { header: 'Remarks', render: r => <span className="text-xs truncate max-w-xs block" style={{color:'#475569'}}>{r.remarks || '—'}</span> },
    { header: '', render: r => (
      <div className="flex gap-1">
        {isIT && <button onClick={() => { setSelected(r); setForm({...r}); setError(''); setModal('form'); }} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={11}/></button>}
        {isAdmin && <button onClick={() => handleDelete(r.id)} className="btn-ghost px-2 py-1 text-xs" style={{color:'#ef4444'}}><Trash2 size={11}/></button>}
      </div>
    )}
  ];

  const emailColumns = [
    { header: 'Employee', render: r => <div><div className="font-medium text-sm" style={{color:'#e2e8f0'}}>{r.employee?.full_name || '—'}</div></div> },
    { header: 'Email Address', render: r => <span className="font-mono text-xs" style={{color:'#34d399'}}>{r.email_address}</span> },
    { header: 'Type', render: r => <Badge value={r.email_type}/> },
    { header: 'Approved By', render: r => <span style={{color:'#60a5fa'}}>{r.approved_by || '—'}</span> },
    { header: 'Status', render: r => <Badge value={r.status}/> },
    { header: 'Forwarded To', render: r => r.forwarded_to ? <span className="font-mono text-xs" style={{color:'#94a3b8'}}>{r.forwarded_to}</span> : <span style={{color:'#334155'}}>—</span> },
    { header: 'Remarks', render: r => <span className="text-xs truncate max-w-xs block" style={{color:'#475569'}}>{r.remarks || '—'}</span> },
    { header: '', render: r => (
      <div className="flex gap-1">
        {isIT && <button onClick={() => { setSelected(r); setForm({...r}); setError(''); setModal('form'); }} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={11}/></button>}
        {isAdmin && <button onClick={() => handleDelete(r.id)} className="btn-ghost px-2 py-1 text-xs" style={{color:'#ef4444'}}><Trash2 size={11}/></button>}
      </div>
    )}
  ];

  const statuses = tab === 'systems' ? SYS_STATUSES : EMAIL_STATUSES;

  return (
    <div className="space-y-4">
      <PageHeader title="Access Management" count={total}>
        <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13}/></button>
        {isIT && <button onClick={openAdd} className="btn-primary"><Plus size={13}/> Add {tab === 'systems' ? 'System Access' : 'Email ID'}</button>}
      </PageHeader>

      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.06)'}}>
        {[['systems', 'System Logins', <Monitor size={13}/>], ['emails', 'Email IDs', <Mail size={13}/>]].map(([key, label, icon]) => (
          <button key={key} onClick={() => { setTab(key); setSearch(''); setFilterStatus(''); }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{background:tab===key?'rgba(59,130,246,0.2)':'transparent',color:tab===key?'#60a5fa':'#475569',border:tab===key?'1px solid rgba(59,130,246,0.3)':'1px solid transparent'}}>
            {icon}{label}
          </button>
        ))}
      </div>

      <FilterBar hasFilters={!!(search || filterStatus)} onClear={() => { setSearch(''); setFilterStatus(''); }}>
        <SearchInput value={search} onChange={setSearch} placeholder={tab === 'systems' ? 'Search employee, system name, username...' : 'Search employee, email address...'} />
        <FilterSelect value={filterStatus} onChange={setFilterStatus} placeholder="All Statuses" options={statuses.map(s => ({value:s,label:s.replace('_',' ')}))} />
      </FilterBar>

      <Table columns={tab === 'systems' ? sysColumns : emailColumns} data={rows} loading={loading} page={1} pages={1} total={total} onPage={() => {}} />

      {modal === 'form' && tab === 'systems' && (
        <Modal title={selected ? 'Edit System Access' : 'Add System Access'} onClose={() => { setModal(null); setSelected(null); }} size="lg">
          <form onSubmit={handleSave} className="space-y-4">
            <ErrMsg msg={error} />
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Employee *</label>
                <SearchableSelect required value={form.employee_id || ''}
                  onChange={v => setForm({...form, employee_id: v})}
                  options={employees.map(e => ({ value: e.id, label: e.full_name, hint: e.employee_code || undefined }))}
                  placeholder="Select employee"/>
              </div>
              <FF label="System / ERP Name" name="system_name" form={form} setForm={setForm} required placeholder="e.g. SAP, Tally, CRM..." />
              <FF label="Username" name="username" form={form} setForm={setForm} placeholder="Login username" />
              <FF label="Access Level" name="access_level" form={form} setForm={setForm} placeholder="e.g. Admin, View Only..." />
              <FF label="Approved By" name="approved_by" form={form} setForm={setForm} placeholder="Person who approved access" />
              <FS label="Status" name="status" form={form} setForm={setForm} options={SYS_STATUSES.map(s=>({val:s,label:s.replace('_',' ')}))} />
            </div>
            <FF label="Remarks" name="remarks" form={form} setForm={setForm} rows={3} placeholder="Additional notes about this access..." />
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save'}</button>
            </div>
          </form>
        </Modal>
      )}

      {modal === 'form' && tab === 'emails' && (
        <Modal title={selected ? 'Edit Email ID' : 'Add Email ID'} onClose={() => { setModal(null); setSelected(null); }} size="lg">
          <form onSubmit={handleSave} className="space-y-4">
            <ErrMsg msg={error} />
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Employee *</label>
                <SearchableSelect required value={form.employee_id || ''}
                  onChange={v => setForm({...form, employee_id: v})}
                  options={employees.map(e => ({ value: e.id, label: e.full_name, hint: e.employee_code || undefined }))}
                  placeholder="Select employee"/>
              </div>
              <FF label="Email Address" name="email_address" type="email" form={form} setForm={setForm} required placeholder="user@company.com" />
              <FS label="Email Type" name="email_type" form={form} setForm={setForm} options={EMAIL_TYPES.map(t=>({val:t,label:t}))} />
              <FF label="Approved By" name="approved_by" form={form} setForm={setForm} placeholder="Person who approved this email" />
              <FS label="Status" name="status" form={form} setForm={setForm} options={EMAIL_STATUSES.map(s=>({val:s,label:s.replace('_',' ')}))} />
              {form.status === 'forwarded' && <div className="col-span-2"><FF label="Forward To" name="forwarded_to" type="email" form={form} setForm={setForm} placeholder="forward@company.com" /></div>}
            </div>
            <FF label="Remarks" name="remarks" form={form} setForm={setForm} rows={3} placeholder="Additional notes..." />
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
